/**
 * Single source of truth for the user-visible build version + last-deployed
 * timestamp. Bump these two constants together at every release — no
 * env-var juggling, no build-time wiring, one-line PR before deploy.
 *
 * Conventions:
 *  - BUILD_VERSION uses dotted decimals while we're pre-1.0. Bump the
 *    minor for user-visible features, patch for bug-fix releases.
 *  - LAST_DEPLOYED_AT is an ISO 8601 UTC timestamp (date + time) — the
 *    same format the audit trail uses, so it reads consistently.
 *
 * Helper `formatDeployStamp()` returns the same string we render in the
 * UI ("YYYY-MM-DD · HH:MM UTC"), so the badge + tooltip stay in sync.
 */
export const BUILD_VERSION = "0.8.5";
export const LAST_DEPLOYED_AT = "2026-07-02T08:00:00Z";

export function formatDeployStamp(iso = LAST_DEPLOYED_AT) {
  // Defensive: if the constant is ever malformed, fall back to the raw
  // string so the badge degrades gracefully instead of crashing the
  // Landing / Login pages on first paint.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Render in Canberra time (Australia/Sydney) so it matches the company's
  // home jurisdiction. Intl handles AEST/AEDT switching automatically, so
  // the same source-of-truth UTC string reads correctly all year round.
  const fmt = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
    timeZoneName: "short",
  });
  const parts = fmt.formatToParts(d).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const tz = parts.timeZoneName || "AEST";
  return `${parts.year}-${parts.month}-${parts.day} · ${parts.hour}:${parts.minute} ${tz}`;
}

export default { BUILD_VERSION, LAST_DEPLOYED_AT, formatDeployStamp };
